import { Button, Loader, TextInput } from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { ErrorWithContact } from "@components/errors/ErrorWithContact/ErrorWithContact";
import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { KWh, M3 } from "@icons";
import type { UrlParam } from "@router/router.types";
import type { GetExpositionPoint } from "@services/apiEcms/apiEcms.types";
import { useGetExpositionPoint } from "@services/apiEcms/useGetExpositionPoint";
import { usePostMeterReading } from "@services/apiEcms/usePostMeterReading";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterSelfReading.module.css";
import type { PerimeterArcForm } from "./PerimeterSelfReading.types";

const selectDials = ({ dials }: GetExpositionPoint) => dials;

export const PerimeterSelfReading: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);
  const {
    customerId = "",
    contractId = "",
    siteId = "",
    energy,
  } = useParams<UrlParam>();
  const { mutate, isPending } = usePostMeterReading(
    customerId,
    contractId,
    siteId,
  );

  const { data, isLoading, isError } = useGetExpositionPoint(
    {
      customerId,
      contractId,
      siteId,
      readingPage: 1,
    },
    { select: selectDials },
  );

  const { control, handleSubmit } = useForm<PerimeterArcForm>({
    mode: FORM_VALIDATION_MODE,
  });

  const onSubmit = (readingIndexes: PerimeterArcForm) => {
    const formattedIndexes = Object.entries(readingIndexes).map(
      (readingIndex) => ({
        temporalCategory: readingIndex[0],
        value: Number(readingIndex[1]),
      }),
    );

    mutate(formattedIndexes);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError || !data)
    return (
      <ErrorWithContact
        text={t("perimeter.sites.selfReading.error.description")}
        title={t("perimeter.sites.selfReading.error.title")}
      />
    );

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.inputs}>
        {data.map((item) => (
          <Controller
            key={item.categoryShortName}
            control={control}
            name={item.categoryShortName}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                data-testid={
                  energy === "GAS" ? "input-svg-m3" : "input-svg-kWh"
                }
                endIcon={energy === "GAS" ? M3 : KWh}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, {
                    ns: "common",
                    maxDigits: item.wheels,
                  })
                }
                label={item.categoryLongName}
                placeholder={t("perimeter.sites.selfReading.placeholder", {
                  maxDigits: item.wheels,
                })}
                type="number"
              />
            )}
            rules={{
              required: "form.error.required",
              validate: (value) =>
                value.length <= item.wheels || "form.error.tooManyDigits",
            }}
          />
        ))}
      </div>

      <Button isLoading={isPending} type="submit" variant="primary">
        {t("button.confirm", { ns: "common" })}
      </Button>
    </form>
  );
};
